import { useCallback, useEffect, useMemo, useState } from 'react';

export const useTrackSectionOnScroll = (sectionIds: string[], topOffset = 80): string | null => {
  const [currentSectionId, setCurrentSectionId] = useState<string | null>(null);

  const elements = useMemo(() => {
    if (typeof window === 'undefined' || !sectionIds.length) return [];

    return sectionIds.map((id) => document.querySelector(`#${id}`)).filter(Boolean) as Element[];
  }, [sectionIds]);

  const handleScrollTracking = useCallback(() => {
    if (!elements.length) return;

    // Find the section that's currently at the top of the viewport
    const currentSection = elements.reduce(
      (prev, current) => {
        const currentTop = current.getBoundingClientRect().top;

        // If the element is below the offset, it's not the current section
        if (currentTop >= topOffset) return prev;

        // If we don't have a previous section, or this one is closer to the top
        if (!prev || currentTop > prev.getBoundingClientRect().top) {
          return current;
        }

        return prev;
      },
      null as Element | null
    );

    if (!currentSection) {
      setCurrentSectionId(null);
      return;
    }

    const sectionId = currentSection.id;
    setCurrentSectionId(sectionId);
  }, [elements, topOffset]);

  useEffect(() => {
    if (typeof window === 'undefined' || !elements.length) return;

    // Initial check
    handleScrollTracking();

    // Set up scroll listener
    window.addEventListener('scroll', handleScrollTracking);

    return () => {
      window.removeEventListener('scroll', handleScrollTracking);
    };
  }, [handleScrollTracking, elements]);

  return currentSectionId;
};

export default useTrackSectionOnScroll;
